/**
 * ChatPanel.jsx
 * =============
 * Scrollable message feed + pinned input bar.
 *
 * Layout (flex-col, fills available height):
 *   ┌───────────────────────────────┐
 *   │  Message list (flex-1, scroll)│
 *   ├───────────────────────────────┤
 *   │  Input bar (pinned bottom)    │
 *   └───────────────────────────────┘
 *
 * Props
 * -----
 *   messages    – [{role, text, timestamp}, ...] from useTegan
 *   sendCommand – (text: string) => boolean
 *   connected   – boolean
 */

import React, { useState, useRef, useEffect } from 'react';
import ToolForm from './ToolForm';

/** Format a Date.now() timestamp → "HH:MM" */
function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Date separator shown between messages from different days */
function DateSeparator({ timestamp }) {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  let label;
  if (date.toDateString() === today.toDateString()) label = 'Today';
  else if (date.toDateString() === yesterday.toDateString()) label = 'Yesterday';
  else label = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="flex items-center gap-3 my-2">
      <div className="flex-1 h-px bg-[#2a2a2a]" />
      <span className="text-[#444] text-xs">{label}</span>
      <div className="flex-1 h-px bg-[#2a2a2a]" />
    </div>
  );
}

/** Tegan avatar — 28px burnt-orange bordered circle with "T" */
function TeganAvatar() {
  return (
    <div
      className="w-7 h-7 rounded-full border-2 border-[#CC5500] bg-[#2a2a2a]
                 flex items-center justify-center text-xs font-bold text-[#CC5500] shrink-0"
      aria-hidden="true"
    >
      T
    </div>
  );
}

export default function ChatPanel({ messages, sendCommand, connected, activeToolForm, setActiveToolForm }) {
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  // Auto-scroll to newest message.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || !connected) return;
    sendCommand(trimmed);
    setInput('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#1a1a1a]">

      {/* ── Message list ─────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4 flex flex-col gap-4">

        {messages.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-[#444] text-sm select-none">
              Send a command or click a tool to get started
            </p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isUser = msg.role === 'user';
            const showSeparator = i === 0 ||
              new Date(msg.timestamp).toDateString() !== new Date(messages[i - 1].timestamp).toDateString();
            return (
              <React.Fragment key={i}>
                {showSeparator && <DateSeparator timestamp={msg.timestamp} />}
                <div
                  className={`flex items-start gap-2 ${isUser ? 'justify-end' : ''}`}
                >
                  {/* Tegan avatar — left side of Tegan messages only */}
                  {!isUser && <TeganAvatar />}

                  <div className={`flex flex-col gap-1 max-w-[75%] ${isUser ? 'items-end' : 'items-start'}`}>
                    {/* Bubble */}
                    <div
                      className={`px-4 py-2.5 rounded-xl text-sm leading-relaxed break-words ${
                        isUser
                          ? 'bg-[#CC5500] text-white rounded-br-sm'
                          : 'bg-[#1e1e1e] border border-[#2a2a2a] text-white rounded-bl-sm'
                      }`}
                    >
                      {msg.text}
                    </div>
                    {/* Timestamp */}
                    <span className="text-[10px] text-[#555] px-1">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}

        {/* Scroll anchor */}
        <div ref={bottomRef} />
      </div>

      {/* ── Tool form — rendered above input bar when active ─────────── */}
      {activeToolForm && (
        <ToolForm
          tool={activeToolForm}
          onSubmit={(cmd) => { sendCommand(cmd); setActiveToolForm(null); }}
          onCancel={() => setActiveToolForm(null)}
        />
      )}

      {/* ── Input bar ────────────────────────────────────────────────── */}
      <div className="shrink-0 bg-[#161616] border-t border-[#2a2a2a] px-4 py-3 flex gap-2 items-center">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!connected}
          placeholder={
            connected ? 'Type a command and press Enter…' : 'Waiting for connection…'
          }
          className="
            flex-1 bg-[#1e1e1e] border border-[#2a2a2a] text-white text-sm
            placeholder-[#555] rounded px-3 py-2
            focus:border-[#CC5500] focus:outline-none
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors duration-150
          "
        />
        <button
          onClick={handleSend}
          disabled={!connected || !input.trim()}
          className="
            flex-shrink-0 bg-[#CC5500] hover:bg-[#aa4400]
            disabled:opacity-40 disabled:cursor-not-allowed
            text-white text-sm font-medium rounded px-4 py-2
            transition-colors duration-150
            focus:outline-none
          "
        >
          Send
        </button>
      </div>
    </div>
  );
}
